import React, { useState, useEffect } from 'react';
import { FiDownload, FiUpload, FiTrash2, FiRefreshCw, FiClock, FiHardDrive } from 'react-icons/fi';
import { getBackups, createBackup, downloadBackup, restoreBackup, deleteBackup } from '../services/backup';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import toast from 'react-hot-toast';

export default function Backups() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [backups, setBackups] = useState([]);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    setLoading(true);
    try {
      const data = await getBackups();
      setBackups(data.data || []);
    } catch (error) {
      toast.error('Failed to fetch backups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      await createBackup();
      toast.success('Backup created successfully');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to create backup');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      const blob = await downloadBackup(filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download backup');
    }
  };

  const handleRestore = async (filename) => {
    if (!window.confirm(`Are you sure you want to restore backup "${filename}"? This will overwrite current data.`)) return;
    try {
      await restoreBackup(filename);
      toast.success('Backup restored successfully');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to restore backup');
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete backup "${filename}"?`)) return;
    try {
      await deleteBackup(filename);
      toast.success('Backup deleted');
      fetchBackups();
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Database Backups</h1>
        <button
          onClick={handleCreateBackup}
          disabled={creating}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50"
        >
          <FiRefreshCw className={`mr-2 ${creating ? 'animate-spin' : ''}`} />
          {creating ? 'Creating...' : 'Create Backup'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Backups</p>
              <p className="text-2xl font-bold text-gray-800">{backups.length}</p>
            </div>
            <FiHardDrive className="w-8 h-8 text-primary-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Size</p>
              <p className="text-2xl font-bold text-gray-800">
                {formatSize(backups.reduce((sum, b) => sum + b.size, 0))}
              </p>
            </div>
            <FiHardDrive className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Latest Backup</p>
              <p className="text-lg font-semibold text-gray-800">
                {backups[0] ? formatDate(backups[0].created_at) : 'No backups'}
              </p>
            </div>
            <FiClock className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Backups Table */}
      {backups.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-500">
          No backups found. Click "Create Backup" to create your first backup.
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Filename</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Modified</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {backups.map((backup) => (
                  <tr key={backup.filename} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm">{backup.filename}</span>
                    </td>
                    <td className="px-6 py-4 text-sm">{formatSize(backup.size)}</td>
                    <td className="px-6 py-4 text-sm">{formatDate(backup.created_at)}</td>
                    <td className="px-6 py-4 text-sm">{formatDate(backup.modified_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDownload(backup.filename)}
                        className="text-blue-500 hover:text-blue-700 mr-3"
                        title="Download"
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRestore(backup.filename)}
                        className="text-green-500 hover:text-green-700 mr-3"
                        title="Restore"
                      >
                        <FiUpload className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(backup.filename)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
